import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "./Button";
import { cn } from "@/lib/cn";

/**
 * App-wide confirmation modal. Replaces `window.confirm`, which is ugly,
 * inconsistent across browsers, and doesn't follow our design tokens.
 *
 * Usage
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Dismiss this question?",
 *     description: "It will stop appearing in Content gaps.",
 *     confirmLabel: "Dismiss",
 *     variant: "danger",
 *   });
 *   if (!ok) return;
 *
 * Behavior
 *   • Single modal at a time. A new confirm() call while one is open
 *     resolves the previous prompt to false (treated as cancel).
 *   • Pressing Enter confirms, Escape cancels.
 *   • Clicking the backdrop cancels.
 *   • Focus lands on the Confirm button when opened.
 */

interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "danger";
}

interface OpenState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OpenState | null>(null);
  // Keep the latest resolve callback on a ref so close handlers don't get
  // stale during the brief render between state changes.
  const stateRef = useRef<OpenState | null>(null);
  stateRef.current = state;

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      // If there's already an open confirm, treat the older one as cancelled.
      if (stateRef.current) {
        stateRef.current.resolve(false);
      }
      setState({ ...opts, resolve });
    });
  }, []);

  const close = useCallback((result: boolean) => {
    const current = stateRef.current;
    if (!current) return;
    current.resolve(result);
    setState(null);
  }, []);

  // Keyboard handling — Enter/Escape act on whatever's currently open.
  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); close(false); }
      if (e.key === "Enter")  { e.preventDefault(); close(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && <ConfirmDialog state={state} onClose={close} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({ state, onClose }: { state: OpenState; onClose: (v: boolean) => void }) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // Slight delay so the click that opened us doesn't immediately submit.
    const t = setTimeout(() => confirmBtnRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, []);

  const isDanger = state.variant === "danger";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      {/* Backdrop. Click cancels. */}
      <div
        aria-hidden="true"
        onClick={() => onClose(false)}
        className="absolute inset-0 bg-ink/60"
      />

      {/* Dialog card */}
      <div
        className={cn(
          "relative z-10 w-full max-w-md rounded-lg border border-line bg-surface",
          // Subtle scale-in transition — no decorative shadow, just a quick
          // emphasis on appearance.
          "animate-[confirm-in_120ms_ease-out]",
        )}
        // Block click bubbling so internal clicks don't cancel.
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-5">
          {isDanger && (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-danger-soft text-danger">
              <AlertTriangle size={18} strokeWidth={1.75} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 id="confirm-title" className="text-md font-medium text-text-strong">
              {state.title}
            </h2>
            {state.description && (
              <div className="mt-1 text-sm text-text-soft">
                {typeof state.description === "string"
                  ? <p>{state.description}</p>
                  : state.description}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line bg-surface-2 px-5 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onClose(false)}
          >
            {state.cancelLabel ?? "Cancel"}
          </Button>
          <Button
            ref={confirmBtnRef}
            variant={isDanger ? "primary" : "primary"}
            size="sm"
            onClick={() => onClose(true)}
            className={cn(
              isDanger && "bg-danger border-danger hover:bg-danger/90",
            )}
          >
            {state.confirmLabel ?? "Confirm"}
          </Button>
        </div>
      </div>

      {/* Keyframes inline so we don't need a tailwind plugin or a global CSS edit. */}
      <style>{`
        @keyframes confirm-in {
          from { opacity: 0; transform: translateY(4px) scale(0.99); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
      `}</style>
    </div>
  );
}
